// AdminAttestationDetailPage — Detail admin d'une attestation
// Layout compact en 3 colonnes : info + diagnostic + actions

import { useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useShow, useCustomMutation } from '@refinedev/core'
import {
  ArrowLeft,
  Check,
  X,
  Download,
  Loader2,
  Building2,
  User,
  HardHat,
  Calendar,
  Award,
  BarChart3,
  Mail,
  MessageSquare,
  FileCheck,
  Shield,
} from 'lucide-react'
import { CasqueIcons } from '../components/results/CasqueDisplay'
import type { Casque } from '@autodiag/shared'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card'
import { Separator } from '../components/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'

const API_URL = '/api'

// Types matching actual API response from /api/admin/attestations/:id
interface AttestationDetail {
  id: string
  genreGerant: string
  nomGerant: string
  prenomGerant: string
  nomEntreprise: string
  metiers: string[] // String[] from Prisma
  casques: number
  dateDiagnostic: string
  dateValidite: string
  statut: 'en_attente' | 'validee' | 'refusee' | 'generee'
  certificationType: string | null
  certificatPath: string | null
  emailContact: string | null
  commentaire: string | null
  badgeDore: boolean
  createdAt: string
  updatedAt: string
  validatedAt: string | null
  refusedReason: string | null
  entreprise: {
    raisonSociale: string
    siret?: string
    effectif?: number
    adresse?: string
  }
  validatedBy: { prenom: string; nom: string; email: string } | null
  diagnostic?: {
    id: string
    user?: { email: string; prenom: string; nom: string } | null
    resultat?: {
      scoreGlobal: number
      maxGlobal: number
      casques: number
      scoreQ2: number
      maxQ2: number
    }
  }
}

function getStatusBadge(statut: AttestationDetail['statut']) {
  switch (statut) {
    case 'en_attente':
      return (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200">
          En attente de validation
        </Badge>
      )
    case 'validee':
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          Validée
        </Badge>
      )
    case 'refusee':
      return <Badge variant="destructive">Refusée</Badge>
    case 'generee':
      return <Badge variant="secondary">Générée</Badge>
    default:
      return <Badge variant="outline">{statut}</Badge>
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function CasquesRow({ count, size = 'sm' }: { count: number; size?: 'sm' | 'md' }) {
  return (
    <div className="flex items-center gap-2">
      <CasqueIcons count={Math.min(count, 5) as Casque} size={size} />
      <span className="text-sm font-semibold tabular-nums">
        {count}/5
      </span>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  )
}

export default function AdminAttestationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [showValiderDialog, setShowValiderDialog] = useState(false)
  const [showRefuserDialog, setShowRefuserDialog] = useState(false)
  const [motifRefus, setMotifRefus] = useState('')
  const [downloadingPdf, setDownloadingPdf] = useState(false)

  const { query } = useShow<AttestationDetail>({
    resource: 'attestations',
    id,
  })

  const { mutate: validerMutation, mutation: validerState } =
    useCustomMutation()
  const { mutate: refuserMutation, mutation: refuserState } =
    useCustomMutation()
  const isValidating = validerState.isPending
  const isRefusing = refuserState.isPending

  const attestation = query.data?.data
  const isLoading = query.isLoading

  const handleValider = () => {
    validerMutation(
      {
        url: `${API_URL}/admin/attestations/${id}/valider`,
        method: 'put',
        values: {},
      },
      {
        onSuccess: () => {
          setShowValiderDialog(false)
          query.refetch()
        },
      }
    )
  }

  const handleRefuser = () => {
    if (!motifRefus.trim()) return

    refuserMutation(
      {
        url: `${API_URL}/admin/attestations/${id}/refuser`,
        method: 'put',
        values: { refusedReason: motifRefus.trim() },
      },
      {
        onSuccess: () => {
          setShowRefuserDialog(false)
          setMotifRefus('')
          query.refetch()
        },
      }
    )
  }

  const handleBack = () => {
    navigate('/admin/attestations')
  }

  const handleDownloadPdf = useCallback(async () => {
    if (!id) return
    setDownloadingPdf(true)
    try {
      const response = await fetch(
        `${API_URL}/admin/attestations/${id}/pdf`,
        { credentials: 'include' }
      )
      if (!response.ok) throw new Error('Erreur de generation')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download =
        response.headers
          .get('Content-Disposition')
          ?.match(/filename="(.+)"/)?.[1] || `attestation-${id}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Erreur PDF:', err)
    } finally {
      setDownloadingPdf(false)
    }
  }, [id])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!attestation) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground mb-4">
          Attestation introuvable.
        </p>
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="size-4 mr-2" />
          Retour a la liste
        </Button>
      </div>
    )
  }

  const isPending = attestation.statut === 'en_attente'
  const resultat = attestation.diagnostic?.resultat
  const scoreGlobal = resultat?.scoreGlobal
  const maxGlobal = resultat?.maxGlobal
  const pctScore =
    scoreGlobal != null && maxGlobal
      ? Math.round((scoreGlobal / maxGlobal) * 100)
      : null

  return (
    <>
      <div className="space-y-6">
        {/* Header — Back + Title + Status + Action buttons */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={handleBack} className="shrink-0">
              <ArrowLeft className="size-4" />
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl font-bold truncate">
                  {attestation.nomEntreprise ||
                    attestation.entreprise?.raisonSociale ||
                    'Attestation'}
                </h1>
                {getStatusBadge(attestation.statut)}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Demande du {formatDate(attestation.createdAt)}
                {attestation.dateValidite && (
                  <> — Validite jusqu'au {formatDate(attestation.dateValidite)}</>
                )}
              </p>
            </div>
          </div>

          {/* Action buttons for pending attestations — in header for quick access */}
          {isPending && (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => setShowValiderDialog(true)}
                disabled={isValidating || isRefusing}
              >
                {isValidating ? (
                  <Loader2 className="size-4 mr-2 animate-spin" />
                ) : (
                  <Check className="size-4 mr-2" />
                )}
                Valider
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowRefuserDialog(true)}
                disabled={isValidating || isRefusing}
              >
                {isRefusing ? (
                  <Loader2 className="size-4 mr-2 animate-spin" />
                ) : (
                  <X className="size-4 mr-2" />
                )}
                Refuser
              </Button>
            </div>
          )}
        </div>

        {/* Main content — 3-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column (2/3) — Informations */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Entreprise */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Building2 className="size-4 text-muted-foreground" />
                      Entreprise
                    </h3>
                    <div className="space-y-2 text-sm">
                      <InfoRow
                        label="Raison sociale"
                        value={attestation.entreprise?.raisonSociale}
                      />
                      <InfoRow
                        label="SIRET"
                        value={attestation.entreprise?.siret}
                      />
                      <InfoRow
                        label="Effectif"
                        value={
                          attestation.entreprise?.effectif != null
                            ? `${attestation.entreprise.effectif} salaries`
                            : undefined
                        }
                      />
                      <InfoRow
                        label="Adresse"
                        value={attestation.entreprise?.adresse}
                      />
                    </div>
                  </div>

                  {/* Gerant + Utilisateur + Certification */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <User className="size-4 text-muted-foreground" />
                      Gérant
                    </h3>
                    <div className="space-y-2 text-sm">
                      <InfoRow
                        label="Nom"
                        value={`${attestation.genreGerant ?? ''} ${attestation.prenomGerant} ${attestation.nomGerant}`.trim()}
                      />
                      {attestation.diagnostic?.user && (
                        <>
                          <InfoRow
                            label="Utilisateur"
                            value={`${attestation.diagnostic.user.prenom} ${attestation.diagnostic.user.nom}`}
                          />
                          <div className="flex justify-between gap-4">
                            <span className="text-muted-foreground shrink-0">Email</span>
                            <a
                              href={`mailto:${attestation.diagnostic.user.email}`}
                              className="font-medium text-primary hover:underline text-right truncate"
                            >
                              {attestation.diagnostic.user.email}
                            </a>
                          </div>
                        </>
                      )}
                    </div>

                    <Separator className="my-3" />

                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Award className="size-4 text-muted-foreground" />
                      Certification
                    </h3>
                    <div className="space-y-2 text-sm">
                      <InfoRow
                        label="Type"
                        value={attestation.certificationType || 'Aucune'}
                      />
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-muted-foreground">Casques</span>
                        <CasquesRow count={attestation.casques} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Metiers */}
                {attestation.metiers && attestation.metiers.length > 0 && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
                        <HardHat className="size-4 text-muted-foreground" />
                        Métiers évalués
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {attestation.metiers.map((metier) => (
                          <Badge key={metier} variant="secondary">
                            {metier}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Contact + Commentaire */}
                {(attestation.emailContact || attestation.commentaire) && (
                  <>
                    <Separator className="my-4" />
                    <div className="space-y-3 text-sm">
                      {attestation.emailContact && (
                        <div className="flex items-start gap-2">
                          <Mail className="size-4 text-muted-foreground mt-0.5 shrink-0" />
                          <div>
                            <span className="text-muted-foreground">
                              Contact :{' '}
                            </span>
                            <a
                              href={`mailto:${attestation.emailContact}`}
                              className="font-medium text-primary hover:underline"
                            >
                              {attestation.emailContact}
                            </a>
                          </div>
                        </div>
                      )}
                      {attestation.commentaire && (
                        <div className="flex items-start gap-2">
                          <MessageSquare className="size-4 text-muted-foreground mt-0.5 shrink-0" />
                          <div>
                            <span className="text-muted-foreground">
                              Commentaire :{' '}
                            </span>
                            <span className="font-medium">
                              {attestation.commentaire}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Historique de traitement */}
            {!isPending && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Calendar className="size-4 text-muted-foreground" />
                    Historique de traitement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {attestation.statut === 'validee' && (
                      <>
                        {attestation.validatedBy && (
                          <InfoRow
                            label="Valide par"
                            value={`${attestation.validatedBy.prenom} ${attestation.validatedBy.nom}`}
                          />
                        )}
                        {attestation.validatedAt && (
                          <InfoRow
                            label="Date de validation"
                            value={formatDateTime(attestation.validatedAt)}
                          />
                        )}
                      </>
                    )}
                    {attestation.statut === 'refusee' && (
                      <>
                        {attestation.validatedBy && (
                          <InfoRow
                            label="Refuse par"
                            value={`${attestation.validatedBy.prenom} ${attestation.validatedBy.nom}`}
                          />
                        )}
                        {attestation.validatedAt && (
                          <InfoRow
                            label="Date de refus"
                            value={formatDateTime(attestation.validatedAt)}
                          />
                        )}
                        {attestation.refusedReason && (
                          <div>
                            <p className="text-muted-foreground mb-1">
                              Motif du refus
                            </p>
                            <p className="font-medium text-destructive">
                              {attestation.refusedReason}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                    {attestation.statut === 'generee' && (
                      <InfoRow
                        label="Statut"
                        value={`Générée le ${formatDateTime(attestation.updatedAt)}`}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right column (1/3) — Diagnostic + Downloads */}
          <div className="space-y-6">
            {/* Diagnostic score */}
            {resultat && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <BarChart3 className="size-4 text-muted-foreground" />
                    Diagnostic
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Score global */}
                    <div className="text-center">
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold text-primary tabular-nums">
                          {Math.round(scoreGlobal!)}
                        </span>
                        <span className="text-lg text-muted-foreground">
                          /{Math.round(maxGlobal!)}
                        </span>
                      </div>
                      {pctScore !== null && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Score global ({pctScore}%)
                        </p>
                      )}
                    </div>

                    <Separator />

                    {/* Casques diagnostic */}
                    <div className="flex flex-col items-center gap-2">
                      <CasquesRow count={resultat.casques} size="md" />
                      <p className="text-xs text-muted-foreground">
                        Niveau obtenu
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Documents */}
            {(attestation.certificatPath ||
              attestation.statut === 'validee' ||
              attestation.statut === 'generee') && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileCheck className="size-4 text-muted-foreground" />
                    Documents
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {attestation.certificatPath && (
                    <Button variant="outline" className="w-full justify-start" asChild>
                      <a
                        href={`${API_URL}/admin/attestations/certificates/${attestation.certificatPath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Shield className="size-4 mr-2" />
                        Certificat {attestation.certificationType}
                      </a>
                    </Button>
                  )}
                  {(attestation.statut === 'validee' ||
                    attestation.statut === 'generee') && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={handleDownloadPdf}
                      disabled={downloadingPdf}
                    >
                      {downloadingPdf ? (
                        <Loader2 className="size-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="size-4 mr-2" />
                      )}
                      {downloadingPdf
                        ? 'Generation...'
                        : 'Attestation PDF'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Badge dore indicator */}
            {attestation.badgeDore && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
                <Award className="size-5 text-amber-600 shrink-0" />
                <span className="font-medium text-amber-800">
                  Badge doré attribué
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dialog de confirmation de validation */}
      <AlertDialog
        open={showValiderDialog}
        onOpenChange={setShowValiderDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Valider cette attestation ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'entreprise{' '}
              <strong>
                {attestation.nomEntreprise ||
                  attestation.entreprise?.raisonSociale}
              </strong>{' '}
              recevra son attestation de niveau {attestation.casques} casque
              {attestation.casques > 1 ? 's' : ''}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isValidating}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleValider}
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={isValidating}
            >
              {isValidating ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  Validation...
                </>
              ) : (
                <>
                  <Check className="size-4 mr-2" />
                  Confirmer la validation
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de refus avec motif obligatoire */}
      <Dialog open={showRefuserDialog} onOpenChange={setShowRefuserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refuser cette attestation</DialogTitle>
            <DialogDescription>
              Veuillez indiquer le motif du refus. Il sera affiché à
              l'entreprise sur son tableau de bord d'attestations.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label
              htmlFor="motif-refus"
              className="block text-sm font-medium mb-2"
            >
              Motif du refus <span className="text-destructive">*</span>
            </label>
            <textarea
              id="motif-refus"
              className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              placeholder="Expliquez les raisons du refus..."
              value={motifRefus}
              onChange={(e) => setMotifRefus(e.target.value)}
              disabled={isRefusing}
            />
            {motifRefus.trim() === '' && (
              <p className="text-xs text-muted-foreground mt-1">
                Le motif est obligatoire pour refuser une attestation.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRefuserDialog(false)
                setMotifRefus('')
              }}
              disabled={isRefusing}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleRefuser}
              disabled={!motifRefus.trim() || isRefusing}
            >
              {isRefusing ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <X className="size-4 mr-2" />
                  Confirmer le refus
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
